import { ListTodo, Workflow } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import type { CreatedTaskReference } from "@/lib/created-task";
import { cn } from "@/lib/utils";

export type CreatedTaskPillProps = {
  task: CreatedTaskReference;
  className?: string;
} | {
  kind: "workflow-run";
  taskId?: string | null;
  onClick?: () => void;
  className?: string;
};

/** A compact navigation affordance for a created task or workflow run. */
export function CreatedTaskPill(props: CreatedTaskPillProps) {
  const { t } = useTranslation("chat");
  const isWorkflowRun = "kind" in props;
  const title = isWorkflowRun ? t("workflowRun.openRun") : props.task.title ?? t("tool.createdTask");
  const content = <>
    {isWorkflowRun
      ? <Workflow aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
      : <ListTodo aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />}
    <span className="truncate">{title}</span>
  </>;

  return (
    <Badge
      asChild
      variant="outline"
      className={cn(
        "max-w-[200px] gap-1.5 bg-muted/40 px-2 py-1 font-medium hover:bg-muted",
        "focus:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        props.className,
      )}
    >
      {isWorkflowRun
        ? props.taskId && props.onClick
          ? <button type="button" title={title} aria-label={title} data-testid="workflow-run-pill"
              data-task-id={props.taskId} onClick={props.onClick}>{content}</button>
          : <span title={title} data-testid="workflow-run-pill">{content}</span>
        : <Link
            href={`/project/${encodeURIComponent(props.task.projectId)}?task=${encodeURIComponent(props.task.id)}`}
            title={title}
            aria-label={t("tool.openCreatedTask", { title })}
            data-testid="created-task-pill"
            data-task-id={props.task.id}
          >{content}</Link>}
    </Badge>
  );
}
